/**
 * Product-analytics consent gate (RFC-0974 §13).
 *
 * The ConsentProvider calls this on every consent change so the event emitter's
 * consent decision stays current. This is the console's ONLY non-essential
 * collector (Sentry removed in RFC-1172). The emitter is always loaded but
 * no-ops unless permitted; this just flips the emitter's gate and drains its
 * queue on withdrawal.
 */

import { setAnalyticsConsent } from '../analytics/consentGate'
import {
  CONSENT_JURISDICTION,
  CONSENT_POLICY_VERSION,
  type ConsentCategories,
  type Jurisdiction,
} from './consentTypes'
import { isCategoryPermitted } from './consentResolution'
import { readConsent, readGpcSignal } from './consentStorage'

/**
 * Sync the emitter's analytics-consent decision. Returns whether analytics is
 * currently permitted. A true→false transition drains the emitter queue.
 */
export function syncAnalyticsConsent(
  jurisdiction: Jurisdiction,
  cats: ConsentCategories | undefined,
  gpc: boolean
): boolean {
  const permitted = isCategoryPermitted('analytics', jurisdiction, cats, gpc)
  setAnalyticsConsent(permitted)
  return permitted
}

/**
 * Initialize the emit gate from ALREADY-STORED consent at module load — before
 * any React render or effect runs. Without this, a returning consented user's
 * mount-time events (e.g. `landing_page_view`) are dropped: child effects run
 * before the `ConsentProvider` effect that would flip the still-default-denied
 * gate. Reading storage (never writing) is consent-safe. Best-effort — any
 * failure leaves the gate at its default-deny.
 */
export function initAnalyticsConsentFromStorage(): void {
  try {
    const stored = readConsent()
    const consent = stored && stored.v === CONSENT_POLICY_VERSION ? stored : null
    setAnalyticsConsent(
      isCategoryPermitted('analytics', CONSENT_JURISDICTION, consent?.cats, readGpcSignal())
    )
  } catch {
    /* default-deny */
  }
}

// Run at import — the analyticsGate module is imported by ConsentProvider, which
// is loaded before ReactDOM renders, so the gate reflects stored consent before
// the first component mounts.
initAnalyticsConsentFromStorage()

/** Deny analytics + drain (used when acknowledgment is absent/withdrawn). */
export function denyAnalytics(): void {
  setAnalyticsConsent(false)
}
